import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
  categories as defaultCategoriesMap,
  categoryOrder as defaultCategoryOrder,
  products as mockProducts,
  Product,
} from '../mocks/products';

export interface CategoryItem {
  id: string;
  title: string;
  description: string;
  isCustom?: boolean;
  isDefault?: boolean;
}

interface CategoryContextValue {
  categoryList: CategoryItem[];
  addCategory: (title: string, description: string) => string;
  moveCategory: (id: string, direction: 'up' | 'down') => void;
  moveCategoryToIndex: (fromIndex: number, toIndex: number) => void;
  reorderCategories: (newList: CategoryItem[]) => void;
  deleteCategory: (id: string) => void;
  dynamicProducts: Product[];
  addProduct: (product: Product) => void;
  updateProduct: (product: Product) => void;
  deleteProduct: (id: string) => void;
  getProductById: (id: string) => Product | undefined;
  reorderProducts: (categoryId: string, newOrder: Product[]) => void;
}

const CategoryContext = createContext<CategoryContextValue | null>(null);

const STORAGE_KEY_CATEGORIES = 'app_categories';
const STORAGE_KEY_PRODUCTS = 'app_custom_products';
const STORAGE_KEY_PRODUCT_ORDER = 'app_product_order';
const STORAGE_KEY_DELETED_PRODUCTS = 'app_deleted_products';

// 기본 카테고리들
const defaultCategories: CategoryItem[] = defaultCategoryOrder.map((key) => ({
  id: key,
  title: defaultCategoriesMap[key].title,
  description: defaultCategoriesMap[key].description,
  isCustom: false,
  isDefault: false,
}));

// "그 외 다른 제품" 기본 카테고리
const OTHER_PRODUCTS_CATEGORY: CategoryItem = {
  id: 'other_products',
  title: '그 외 다른 제품',
  description: '기타 제품들을 모아둔 카테고리입니다.',
  isCustom: false,
  isDefault: true,
};

function loadCategories(): CategoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CATEGORIES);
    if (!raw) return [...defaultCategories, OTHER_PRODUCTS_CATEGORY];
    const parsed: CategoryItem[] = JSON.parse(raw);
    // 기본 카테고리가 빠져있으면 보완
    const ids = parsed.map((c) => c.id);
    const missing = defaultCategories.filter((c) => !ids.includes(c.id));
    const hasOtherCategory = parsed.some((c) => c.id === OTHER_PRODUCTS_CATEGORY.id);
    
    const result = [...parsed, ...missing.filter((m) => !ids.includes(m.id))];
    if (!hasOtherCategory) {
      result.push(OTHER_PRODUCTS_CATEGORY);
    }
    return result;
  } catch {
    return [...defaultCategories, OTHER_PRODUCTS_CATEGORY];
  }
}

function loadCustomProducts(): Product[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PRODUCTS);
    if (!raw) return [];
    return JSON.parse(raw) as Product[];
  } catch {
    return [];
  }
}

// 제품 순서 로드
function loadProductOrder(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PRODUCT_ORDER);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, string[]>;
  } catch {
    return {};
  }
}

// 제품 순서 저장
function saveProductOrder(order: Record<string, string[]>) {
  try {
    localStorage.setItem(STORAGE_KEY_PRODUCT_ORDER, JSON.stringify(order));
  } catch {
    // ignore
  }
}

// 삭제된 제품 ID 목록 로드
function loadDeletedProductIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DELETED_PRODUCTS);
    if (!raw) return [];
    return JSON.parse(raw) as string[];
  } catch {
    return [];
  }
}

// 삭제된 제품 ID 목록 저장
function saveDeletedProductIds(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY_DELETED_PRODUCTS, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

export function CategoryProvider({ children }: { children: React.ReactNode }) {
  const [categoryList, setCategoryList] = useState<CategoryItem[]>(loadCategories);

  // mockProducts + 커스텀 등록 상품 합산
  const [customProducts, setCustomProducts] = useState<Product[]>(loadCustomProducts);
  
  // 제품 순서 상태
  const [productOrder, setProductOrder] = useState<Record<string, string[]>>(loadProductOrder);

  // 삭제된 제품 ID 목록 (mock 상품 포함 완전 삭제 처리)
  const [deletedProductIds, setDeletedProductIds] = useState<string[]>(loadDeletedProductIds);

  // 전체 상품 = mock + 커스텀 override 적용 - 삭제된 항목 제외
  // customProducts에 같은 id가 있으면 수정된 버전(customProducts)을 우선 사용
  const allProducts: Product[] = [
    ...mockProducts.map((mp) => {
      const override = customProducts.find((cp) => cp.id === mp.id);
      return override ?? mp;
    }),
    ...customProducts.filter((cp) => !mockProducts.find((mp) => mp.id === cp.id)),
  ].filter((p) => !deletedProductIds.includes(p.id));
  
  // 카테고리별 순서 적용된 제품 목록
  const dynamicProducts: Product[] = useMemo(() => {
    const orderedProducts: Product[] = [];
    const usedIds = new Set<string>();
    
    // 카테고리 순서대로 정렬
    categoryList.forEach((category) => {
      const categoryProducts = allProducts.filter((p) => p.category === category.id);
      const order = productOrder[category.id] || [];
      
      // 저장된 순서대로 정렬
      const sorted = [...categoryProducts].sort((a, b) => {
        const aIndex = order.indexOf(a.id);
        const bIndex = order.indexOf(b.id);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
      
      sorted.forEach((p) => {
        if (!usedIds.has(p.id)) {
          orderedProducts.push(p);
          usedIds.add(p.id);
        }
      });
    });
    
    // 순서에 없는 나머지 제품들 추가
    allProducts.forEach((p) => {
      if (!usedIds.has(p.id)) {
        orderedProducts.push(p);
      }
    });
    
    return orderedProducts;
  }, [allProducts, productOrder, categoryList]);

  // categoryList 변경 시 localStorage 저장
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_CATEGORIES, JSON.stringify(categoryList));
  }, [categoryList]);

  // customProducts 변경 시 localStorage 저장
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PRODUCTS, JSON.stringify(customProducts));
  }, [customProducts]);

  const addCategory = useCallback((title: string, description: string): string => {
    const newId = `custom_${Date.now()}`;
    setCategoryList((prev) => [
      ...prev,
      { id: newId, title, description, isCustom: true },
    ]);
    return newId;
  }, []);

  const moveCategory = useCallback((id: string, direction: 'up' | 'down') => {
    setCategoryList((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.length - 1) return prev;
      const next = [...prev];
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return next;
    });
  }, []);

  const reorderCategories = useCallback((newList: CategoryItem[]) => {
    setCategoryList(newList);
  }, []);

  const moveCategoryToIndex = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setCategoryList((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  // 카테고리 삭제 - 해당 제품들은 "그 외 다른 제품"으로 이동
  const deleteCategory = useCallback((id: string) => {
    // 기본 카테고리는 삭제 불가
    const category = categoryList.find((c) => c.id === id);
    if (category?.isDefault) return;
    
    setCategoryList((prev) => prev.filter((c) => c.id !== id));
    
    // 해당 카테고리의 제품들을 "그 외 다른 제품"으로 변경
    setCustomProducts((prev) => {
      const updated = prev.map((p) => {
        if (p.category === id) {
          return { ...p, category: OTHER_PRODUCTS_CATEGORY.id };
        }
        return p;
      });
      return updated;
    });
    
    // 해당 카테고리의 제품 순서도 삭제
    setProductOrder((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, [categoryList]);

  // 제품 순서 변경
  const reorderProducts = useCallback((categoryId: string, newOrder: Product[]) => {
    const productIds = newOrder.map((p) => p.id);
    setProductOrder((prev) => {
      const next = { ...prev, [categoryId]: productIds };
      saveProductOrder(next);
      return next;
    });
  }, []);

  const addProduct = useCallback((product: Product) => {
    setCustomProducts((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) return prev.map((p) => (p.id === product.id ? product : p));
      return [...prev, product];
    });
  }, []);

  const updateProduct = useCallback((product: Product) => {
    // mock 상품이면 customProducts에 override로 저장
    setCustomProducts((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) return prev.map((p) => (p.id === product.id ? product : p));
      return [...prev, product];
    });
  }, []);

  const deleteProduct = useCallback((id: string) => {
    // customProducts에서 제거
    setCustomProducts((prev) => prev.filter((p) => p.id !== id));
    // 삭제 ID 목록에 추가 (mock 상품도 완전 삭제 처리)
    setDeletedProductIds((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      saveDeletedProductIds(next);
      return next;
    });
    // 제품 순서에서도 제거
    setProductOrder((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((catId) => {
        next[catId] = next[catId].filter((pid) => pid !== id);
      });
      saveProductOrder(next);
      return next;
    });
  }, []);

  const getProductById = useCallback(
    (id: string): Product | undefined => {
      return dynamicProducts.find((p) => p.id === id);
    },
    [dynamicProducts]
  );

  return (
    <CategoryContext.Provider
      value={{
        categoryList,
        addCategory,
        moveCategory,
        moveCategoryToIndex,
        reorderCategories,
        deleteCategory,
        dynamicProducts,
        addProduct,
        updateProduct,
        deleteProduct,
        getProductById,
        reorderProducts,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
}

export function useCategoryContext() {
  const ctx = useContext(CategoryContext);
  if (!ctx) throw new Error('useCategoryContext must be used within CategoryProvider');
  return ctx;
}
